import { create } from 'zustand';
import toolsService from '../services/toolsService';
import { useAuthStore } from './authStore';

// Local cache so favorites render instantly on load and work for guests /
// offline. The server is the source of truth for logged-in users.
const CACHE_KEY = 'sohoztools:favs';

function loadCache() {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (raw) return new Set(JSON.parse(raw));
    } catch { /* ignore */ }
    return new Set();
}

function saveCache(set) {
    try { localStorage.setItem(CACHE_KEY, JSON.stringify([...set])); } catch { /* ignore */ }
}

/**
 * Per-user tool favorites, backed by /api/tool-favorites with a localStorage
 * cache. Toggling is optimistic; a failed server call rolls back.
 */
export const useToolFavoritesStore = create((set, get) => ({
    favorites: loadCache(),   // Set<slug>
    loaded: false,

    isFavorite: (slug) => get().favorites.has(slug),

    /** Fetch the authoritative list from the server (once per session). */
    load: async () => {
        if (get().loaded) return;
        if (!useAuthStore.getState().token) { set({ loaded: true }); return; }
        try {
            const rows = await toolsService.getFavorites();
            const next = new Set(rows.map((r) => r.toolSlug));
            saveCache(next);
            set({ favorites: next, loaded: true });
        } catch {
            // Keep the cached set; mark loaded so we don't spin.
            set({ loaded: true });
        }
    },

    /** Optimistically toggle, then sync with the server (rollback on failure). */
    toggle: async (slug) => {
        const prev = get().favorites;
        const next = new Set(prev);
        next.has(slug) ? next.delete(slug) : next.add(slug);
        set({ favorites: next });
        saveCache(next);

        if (!useAuthStore.getState().token) return; // guest: cache-only

        try {
            const res = await toolsService.toggleFavorite(slug);
            // Reconcile with the server's authoritative state.
            const synced = new Set(get().favorites);
            res?.isFavorite ? synced.add(slug) : synced.delete(slug);
            saveCache(synced);
            set({ favorites: synced });
        } catch {
            set({ favorites: prev });   // rollback
            saveCache(prev);
        }
    },
}));
